// Updated to use direct API call
import type { SendSmsRequest, SendSmsResponse } from "@/app/(authenticated)/communication/_modules/types/sendSms.types";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { showToast } from "@/app/_module/utils/show-toast";

// Clear, actionable message shown when the user has no purchased phone number
// to text from — mirrors the call flow's "Phone Number Required" guard.
const NO_NUMBER_SMS_MESSAGE =
  "You need to purchase a phone number before you can send texts. Go to Settings → Phone Numbers to buy one.";

// The backend signals "no purchased number" a few ways (an internal sentinel
// code or a raw message containing a user UUID). Match any of them so we can
// swap in the friendly message above.
const isNoNumberError = (msg?: string): boolean =>
  !!msg &&
  /NO_PURCHASED_NUMBERS|no available phone numbers|no phone number available/i.test(
    msg,
  );

export interface PreviewFromNumberResponse {
  fromNumber: string;
  matchType: string;       // conversation_thread | recent_call | smart_match | sole_prop_a2p_override
  explanation: string;
}

/**
 * Ask the backend what from-number it would auto-select for a given recipient.
 * Used by the SMS modal so the UI default matches what would actually send.
 * Returns null if backend can't resolve (no purchased numbers, network error, etc.).
 */
export const previewSmsFromNumber = async (toNumber: string): Promise<PreviewFromNumberResponse | null> => {
  try {
    const response = await api.get({
      url: getApiUrl(`/api/communication/sms/preview-from-number?to=${encodeURIComponent(toNumber)}`),
      auth: true,
    });
    const wrapped = (response as any)?.data ?? response;
    const data = wrapped?.data ?? null;
    if (!data || !data.fromNumber) return null;
    return data as PreviewFromNumberResponse;
  } catch {
    return null;
  }
};

/**
 * Check if the current user is an Agency Admin who is blocked from calling/SMS.
 * Returns true only if user is admin AND the agency has NOT enabled admin calling.
 */
const isAdminBlockedFromCalling = async (): Promise<boolean> => {
  try {
    const raw = sessionStorage.getItem('insuracentral_user_context');
    if (!raw) return false;
    const ctx = JSON.parse(raw);
    const role = (ctx?.currentAgency?.role || ctx?.user?.agencyRole || ctx?.agencyRole || ctx?.user?.currentAgencyUser?.role || '').toLowerCase();
    if (role !== 'admin') return false;

    // Check if agency allows admin calling
    const agencyId = ctx?.agencyId || ctx?.currentAgency?.agencyId;
    if (!agencyId) return true; // No agency = block by default

    const res = await api.get({ url: getApiUrl(endpoint.agency.getAdminCallingSettings(agencyId)) });
    const data = (res as any)?.data?.data ?? (res as any)?.data ?? res;
    return data?.allowAdminCalling !== true;
  } catch {
    return false; // On error, don't block (backend will enforce)
  }
};

export const sendSms = async (data: SendSmsRequest): Promise<SendSmsResponse | null> => {
  // Block Agency Admins from sending SMS if agency hasn't enabled admin calling
  if (await isAdminBlockedFromCalling()) {
    showToast("Agency Admins cannot send SMS. Contact your agency owner to enable this.", "error");
    return null;
  }

  try {
    const response = await api.post<SendSmsResponse>({
      url: getApiUrl(endpoint.communication.sendSms),
      body: {
        to: data.to,
        text: data.text,
        from: data.from, // Honor explicit from-number picked in the UI; backend respects it (sole-prop A2P override still applies)
        fromCountry: data.fromCountry,
        useUserPhone: data.useUserPhone,
        mediaUrl: data.mediaUrl, // MMS media URL
      },
      auth: true,
    });

    // Get the actual response data (axios wraps it in .data)
    const result = response.data;

    // Check if A2P verification is required
    if (result.statusCode === 403 && result.data?.a2pVerification?.required) {
      showToast(
        "A2P verification required. Please complete your A2P registration in Settings > Phone Numbers to send SMS.",
        "error"
      );
      return null;
    }

    // Check for other non-success responses
    if (result.statusCode < 200 || result.statusCode >= 300) {
      if (isNoNumberError(result.message)) {
        showToast(NO_NUMBER_SMS_MESSAGE, "error");
        return null;
      }
      showToast(result.message || "Failed to send SMS", "error");
      return null;
    }

    return result;
  } catch (error: any) {
    // Handle axios errors
    const raw = error?.response?.data?.message || error?.message || "";
    // No purchased number → show a clear, actionable message (matching the
    // call flow's "Phone Number Required" guard) instead of the raw backend
    // string ("NO_PURCHASED_NUMBERS" code / "No available phone numbers for
    // user <uuid>"), which tells the user nothing.
    if (isNoNumberError(raw)) {
      showToast(NO_NUMBER_SMS_MESSAGE, "error");
      return null;
    }
    const errorMessage = raw || "Failed to send SMS";
    showToast(errorMessage, "error");
    return null;
  }
};

